import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const AdminProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [profileData, setProfileData] = useState({
    email: '',
    full_name: '',
    backup_email: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProfileData({
          email: data.admin.email || '',
          full_name: data.admin.full_name || '',
          backup_email: data.admin.backup_email || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: profileData.full_name,
          backup_email: profileData.backup_email
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to change password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!profileData.backup_email) {
      setMessage({ type: 'error', text: 'Please set a backup email first to use password reset' });
      return;
    }

    setSendingReset(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${backendUrl}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profileData.email })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password reset link sent to your backup email' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to send reset link' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send reset link' });
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold text-[#111827]">Admin Profile</h1>
          <p className="text-[#6B7280] mt-1">Manage your account settings and password</p>
        </div>

        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Profile Information */}
        <form onSubmit={handleUpdateProfile} className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-bold text-[#111827]">Profile Information</h2>
          
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Email</label>
            <input
              type="email"
              value={profileData.email}
              disabled
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-gray-50 text-[#6B7280]"
            />
            <p className="text-xs text-[#6B7280] mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Full Name</label>
            <input
              type="text"
              value={profileData.full_name}
              onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Backup Email</label>
            <input
              type="email"
              value={profileData.backup_email}
              onChange={(e) => setProfileData({...profileData, backup_email: e.target.value})}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              placeholder="backup@example.com"
            />
            <p className="text-xs text-[#6B7280] mt-1">Used for password recovery</p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Update Profile'}
          </button>
        </form>

        {/* Change Password */}
        <form onSubmit={handleChangePassword} className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-bold text-[#111827]">Change Password</h2>
          
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Current Password</label>
            <input
              type="password"
              required
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">New Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            />
            <p className="text-xs text-[#6B7280] mt-1">Minimum 8 characters</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Confirm New Password</label>
            <input
              type="password"
              required
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {saving ? 'Changing...' : 'Change Password'}
            </button>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={sendingReset}
              className="px-6 py-2 text-[#1E40AF] hover:bg-[#EEF2FF] font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {sendingReset ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminProfile;
